/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useContext } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import UserContext from '../context/UserContext';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import '../styles/library.css';
import Breadcrumb from '../components/Breadcrumb';
import UploadPdfModal from '../components/UploadPdfModal';
import PdfViewerModal from '../components/PdfViewerModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faFlag, faTrash, faUpload, faBook } from '@fortawesome/free-solid-svg-icons';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
const PDF_TYPES = ['module', 'magic', 'item', 'bestiary'];
const db = firebase.firestore();

const LibraryPage = () => {
  const { user } = useContext(UserContext);
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [viewerPdf, setViewerPdf] = useState(null);
  const [userDocs, setUserDocs] = useState([]);
  const [dmCampaigns, setDmCampaigns] = useState([]);
  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    const unsub = db.collection('library')
      .orderBy('createdAt', 'desc')
      .onSnapshot(snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUserDocs(all.filter(d => d.authorUid === user?.uid));
        setDocs(all.filter(d =>
          d.access === 'public' ||
          d.authorUid === user?.uid
        ));
      });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user?.uid) return;
    db.collection('campaigns')
      .where('idUserDm', '==', user.uid)
      .where('active', '==', true)
      .get()
      .then(snap => setDmCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user]);

  const countVotes = (doc, type) =>
    Object.values(doc.votes || {}).filter(v => v === type).length;

  const countReports = (doc) =>
    Object.values(doc.reports || {}).filter(Boolean).length;

  const dmCampaignKeys = dmCampaigns.map(c => c.uid || c.id);

  const filtered = docs
    .filter(d => {
      if (filter === 'mine') return d.authorUid === user?.uid;
      if (filter === 'campaigns') return (d.campaignIds || []).some(id => dmCampaignKeys.includes(id));
      if (filter !== 'all') return d.type === filter;
      return true;
    })
    .filter(d => isAdmin || countReports(d) < 3);

  const handleVote = async (doc, vote) => {
    const ref = db.collection('library').doc(doc.id);
    const votes = doc.votes || {};
    const current = votes[user.uid];
    const update = { [`votes.${user.uid}`]: current === vote ? null : vote };
    await ref.update(update);
  };

  const handleReport = async (doc) => {
    const ref = db.collection('library').doc(doc.id);
    const reports = doc.reports || {};
    if (reports[user.uid]) {
      toast.info(i18next.t('library.alreadyReported'));
      return;
    }
    await ref.update({ [`reports.${user.uid}`]: true });
    toast.success(i18next.t('library.reported'));
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(i18next.t('library.confirmDelete'))) return;
    await firebase.storage().ref(`library/${doc.fileName}`).delete().catch(() => {});
    await Promise.all(
      (doc.campaignIds || []).map(campaignId =>
        db.collection('campaignLibrary').doc(campaignId).collection('docs').doc(doc.id).delete().catch(() => {})
      )
    );
    await db.collection('library').doc(doc.id).delete();
    toast.success(i18next.t('library.deleted'));
  };

  return (
    <div className="libraryPage">
      <Breadcrumb items={[{ label: i18next.t('library.title'), path: '/library' }]} />

      <div className="libraryHeader">
        <h1 className="libraryTitle">
          <FontAwesomeIcon icon={faBook} className="libraryTitleIcon" />
          {i18next.t('library.title')}
        </h1>
        {userDocs.length < 3 && (
          <button className="libraryUploadBtn" onClick={() => setShowUpload(true)}>
            <FontAwesomeIcon icon={faUpload} />
            {i18next.t('library.contribute')}
          </button>
        )}
        {userDocs.length >= 3 && (
          <span className="libraryQuotaMsg">{i18next.t('library.quotaReached')}</span>
        )}
      </div>

      <div className="libraryFilters">
        <button
          className={`libraryFilter${filter === 'all' ? ' active' : ''}`}
          onClick={() => setFilter('all')}
        >
          {i18next.t('library.all')}
        </button>
        {PDF_TYPES.map(type => (
          <button
            key={type}
            className={`libraryFilter${filter === type ? ' active' : ''}`}
            onClick={() => setFilter(type)}
          >
            {i18next.t(`library.types.${type}`)}
          </button>
        ))}
        <button
          className={`libraryFilter${filter === 'mine' ? ' active' : ''}`}
          onClick={() => setFilter('mine')}
        >
          {i18next.t('library.filterMine')}
        </button>
        {dmCampaigns.length > 0 && (
          <button
            className={`libraryFilter${filter === 'campaigns' ? ' active' : ''}`}
            onClick={() => setFilter('campaigns')}
          >
            {i18next.t('library.filterCampaigns')}
          </button>
        )}
      </div>

      <div className="libraryGrid">
        {filtered.length === 0 && (
          <p className="libraryEmpty">{i18next.t('library.empty')}</p>
        )}
        {filtered.map(doc => {
          const upvotes = countVotes(doc, 'up');
          const downvotes = countVotes(doc, 'down');
          const userVote = (doc.votes || {})[user?.uid];
          const canDelete = doc.authorUid === user?.uid || isAdmin;
          return (
            <div key={doc.id} className="libraryCard">
              <div className="libraryCardHeader">
                <span className={`libraryCardType libraryCardType--${doc.type}`}>
                  {i18next.t(`library.types.${doc.type}`)}
                </span>
                {doc.access === 'private' && (
                  <span className="libraryCardPrivateBadge">{i18next.t('library.private')}</span>
                )}
                {isAdmin && countReports(doc) > 0 && (
                  <span className="libraryReportBadge">
                    <FontAwesomeIcon icon={faFlag} /> {countReports(doc)}
                  </span>
                )}
              </div>
              <h3 className="libraryCardTitle">{doc.title}</h3>
              <p className="libraryCardAuthor">{doc.authorName}</p>
              {doc.description && <p className="libraryCardDesc">{doc.description}</p>}
              {(doc.campaignIds || []).length > 0 && (
                <span className="libraryCardUsageBadge">
                  {i18next.t('library.usedInCampaigns', { count: (doc.campaignIds || []).length })}
                </span>
              )}
              {dmCampaigns.filter(c => (doc.campaignIds || []).includes(c.id)).map(c => (
                <span key={c.id} className="libraryCardCampaignBadge">✓ {c.name}</span>
              ))}
              <div className="libraryCardActions">
                <button
                  className={`libraryVoteBtn${userVote === 'up' ? ' active' : ''}`}
                  onClick={() => handleVote(doc, 'up')}
                  title={i18next.t('library.upvote')}
                >
                  <FontAwesomeIcon icon={faThumbsUp} />
                  {upvotes > 0 && <span>{upvotes}</span>}
                </button>
                <button
                  className={`libraryVoteBtn${userVote === 'down' ? ' active down' : ''}`}
                  onClick={() => handleVote(doc, 'down')}
                  title={i18next.t('library.downvote')}
                >
                  <FontAwesomeIcon icon={faThumbsDown} />
                  {downvotes > 0 && <span>{downvotes}</span>}
                </button>
                <button
                  className="libraryCardBtn"
                  onClick={() => setViewerPdf(doc)}
                >
                  {i18next.t('library.read')}
                </button>
                {!canDelete && (
                  <button
                    className="libraryReportBtn"
                    onClick={() => handleReport(doc)}
                    title={i18next.t('library.report')}
                  >
                    <FontAwesomeIcon icon={faFlag} />
                  </button>
                )}
                {canDelete && (
                  <button
                    className="libraryDeleteBtn"
                    onClick={() => handleDelete(doc)}
                    title={i18next.t('library.delete')}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showUpload && (
        <UploadPdfModal
          user={user}
          userDocsCount={userDocs.length}
          onClose={() => setShowUpload(false)}
          onSuccess={() => setShowUpload(false)}
        />
      )}

      {viewerPdf && (
        <PdfViewerModal
          pdf={viewerPdf}
          user={user}
          onClose={() => setViewerPdf(null)}
        />
      )}
    </div>
  );
};

export default LibraryPage;
